import { supabase } from '../lib/supabase';
import { getDeviceInfo, getLocationInfo } from './analytics';

// QR Codes
export const qrCodeService = {
  async getAll() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('qr_codes')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('QR 코드 조회 실패:', error);
      throw error;
    }
  },

  async create(qrCode: {
    name: string;
    url: string;
    qr_image: string;
  }) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('qr_codes')
        .insert({
          user_id: user.id,
          name: qrCode.name,
          url: qrCode.url,
          qr_image: qrCode.qr_image,
          scans: 0,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('QR 코드 생성 실패:', error);
      throw error;
    }
  },

  async update(id: string, updates: { scans?: number }) {
    try {
      const { error } = await supabase
        .from('qr_codes')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('QR 코드 업데이트 실패:', error);
      throw error;
    }
  },

  async delete(id: string) {
    try {
      const { error } = await supabase
        .from('qr_codes')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('QR 코드 삭제 실패:', error);
      throw error;
    }
  },

  async getStats() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { total: 0, totalScans: 0 };

      const { data, error } = await supabase
        .from('qr_codes')
        .select('scans')
        .eq('user_id', user.id);

      if (error) throw error;

      const totalScans = data?.reduce((sum, qr) => sum + (qr.scans || 0), 0) || 0;
      return { total: data?.length || 0, totalScans };
    } catch (error) {
      console.error('QR 코드 통계 조회 실패:', error);
      return { total: 0, totalScans: 0 };
    }
  },

  async recordScan(qrCodeId: string, userId: string) {
    try {
      const deviceInfo = getDeviceInfo();
      const locationInfo = await getLocationInfo();

      const { error } = await supabase
        .from('qr_scans')
        .insert({
          qr_code_id: qrCodeId,
          user_id: userId,
          ...deviceInfo,
          ...locationInfo,
        });

      if (error) throw error;
    } catch (error) {
      console.error('QR 스캔 기록 실패:', error);
    }
  },

  async getAnalytics(qrCodeId: string) {
    try {
      const { data, error } = await supabase
        .from('qr_scans')
        .select('*')
        .eq('qr_code_id', qrCodeId)
        .order('scanned_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('QR 분석 데이터 조회 실패:', error);
      return [];
    }
  },
};

// Short Links
export const shortLinkService = {
  async getAll() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('short_links')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('짧은 링크 조회 실패:', error);
      throw error;
    }
  },

  async create(link: {
    name: string;
    original_url: string;
    short_code: string;
  }) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('short_links')
        .insert({
          user_id: user.id,
          name: link.name,
          original_url: link.original_url,
          short_code: link.short_code,
          clicks: 0,
        })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          throw new Error('이 짧은 코드는 이미 사용 중입니다');
        }
        throw error;
      }
      return data;
    } catch (error) {
      console.error('짧은 링크 생성 실패:', error);
      throw error;
    }
  },

  async getByCode(shortCode: string) {
    try {
      const { data, error } = await supabase
        .from('short_links')
        .select('*')
        .eq('short_code', shortCode)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        throw error;
      }
      return data;
    } catch (error) {
      console.error('짧은 링크 조회 실패:', error);
      return null;
    }
  },

  async incrementClicks(id: string, currentClicks: number) {
    try {
      const { error } = await supabase
        .from('short_links')
        .update({ clicks: currentClicks + 1 })
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('클릭 수 증가 실패:', error);
    }
  },

  async delete(id: string) {
    try {
      const { error } = await supabase
        .from('short_links')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('짧은 링크 삭제 실패:', error);
      throw error;
    }
  },

  async getStats() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { total: 0, totalClicks: 0 };

      const { data, error } = await supabase
        .from('short_links')
        .select('clicks')
        .eq('user_id', user.id);

      if (error) throw error;

      const totalClicks = data?.reduce((sum, link) => sum + (link.clicks || 0), 0) || 0;
      return { total: data?.length || 0, totalClicks };
    } catch (error) {
      console.error('짧은 링크 통계 조회 실패:', error);
      return { total: 0, totalClicks: 0 };
    }
  },

  async recordClick(shortLinkId: string, userId: string) {
    try {
      const deviceInfo = getDeviceInfo();
      const locationInfo = await getLocationInfo();

      const { error } = await supabase
        .from('link_clicks')
        .insert({
          short_link_id: shortLinkId,
          user_id: userId,
          ...deviceInfo,
          ...locationInfo,
        });

      if (error) throw error;
    } catch (error) {
      console.error('링크 클릭 기록 실패:', error);
    }
  },

  async getAnalytics(shortLinkId: string) {
    try {
      const { data, error } = await supabase
        .from('link_clicks')
        .select('*')
        .eq('short_link_id', shortLinkId)
        .order('clicked_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('링크 분석 데이터 조회 실패:', error);
      return [];
    }
  },
};

// Bio Links
export const bioLinkService = {
  async getAll() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('bio_links')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('바이오 링크 조회 실패:', error);
      throw error;
    }
  },

  async create(bioLink: {
    username: string;
    title: string;
    bio: string;
    avatar_url?: string;
    links: Array<{ title: string; url: string }>;
    theme: string;
  }) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('bio_links')
        .insert({
          user_id: user.id,
          username: bioLink.username,
          title: bioLink.title,
          bio: bioLink.bio,
          avatar_url: bioLink.avatar_url,
          links: bioLink.links,
          theme: bioLink.theme,
          views: 0,
        })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          throw new Error('이 사용자명은 이미 사용 중입니다');
        }
        throw error;
      }
      return data;
    } catch (error) {
      console.error('바이오 링크 생성 실패:', error);
      throw error;
    }
  },

  async getByUsername(username: string) {
    try {
      const { data, error } = await supabase
        .from('bio_links')
        .select('*')
        .eq('username', username)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        throw error;
      }
      return data;
    } catch (error) {
      console.error('바이오 링크 조회 실패:', error);
      return null;
    }
  },

  async incrementViews(id: string, currentViews: number) {
    try {
      const { error } = await supabase
        .from('bio_links')
        .update({ views: currentViews + 1 })
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('조회 수 증가 실패:', error);
    }
  },

  async delete(id: string) {
    try {
      const { error } = await supabase
        .from('bio_links')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('바이오 링크 삭제 실패:', error);
      throw error;
    }
  },

  async getStats() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { total: 0, totalViews: 0 };

      const { data, error } = await supabase
        .from('bio_links')
        .select('views')
        .eq('user_id', user.id);

      if (error) throw error;

      const totalViews = data?.reduce((sum, bio) => sum + (bio.views || 0), 0) || 0;
      return { total: data?.length || 0, totalViews };
    } catch (error) {
      console.error('바이오 링크 통계 조회 실패:', error);
      return { total: 0, totalViews: 0 };
    }
  },

  async recordView(bioLinkId: string, userId: string) {
    try {
      const deviceInfo = getDeviceInfo();
      const locationInfo = await getLocationInfo();

      const { error } = await supabase
        .from('bio_views')
        .insert({
          bio_link_id: bioLinkId,
          user_id: userId,
          ...deviceInfo,
          ...locationInfo,
        });

      if (error) throw error;
    } catch (error) {
      console.error('바이오 링크 조회 기록 실패:', error);
    }
  },

  async getAnalytics(bioLinkId: string) {
    try {
      const { data, error } = await supabase
        .from('bio_views')
        .select('*')
        .eq('bio_link_id', bioLinkId)
        .order('viewed_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('바이오 링크 분석 데이터 조회 실패:', error);
      return [];
    }
  },
};

// Legacy storage object for backward compatibility (deprecated)
export const storage = {
  getQRCodes: () => [],
  saveQRCode: () => {},
  updateQRCode: () => {},
  deleteQRCode: () => {},
  getShortLinks: () => [],
  saveShortLink: () => {},
  updateShortLink: () => {},
  deleteShortLink: () => {},
  getBioLinks: () => [],
  saveBioLink: () => {},
  updateBioLink: () => {},
  deleteBioLink: () => {},
};
